import React, { useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Box, Tooltip } from 'pcln-design-system'
import TypeAheadContainer, { Dropdown } from '@pcln/typeahead'
import TypeAheadInput from './TypeAheadInput'
import BootstrapDataContext from '../../contexts/BootstrapDataContext'
import { PopoverContainer } from '../Popover'
import { pipe } from 'ramda'

import { scrollToTop, restoreScrollPosition } from '../../utils/popover-scroll-helper'

// TypeAhead input for use with Formik
// this requires some hacking with methods because the typeahead
// API does not use standard React form element props
const TypeAhead = ({
  id,
  icon,
  name,
  label,
  placeholder,
  value,
  valueToString,
  onChange,
  setValue,
  disableShadowState,
  onBlur,
  options = [],
  error,
  popoverButtonProps, // QA autobot id
  allowEmptySelection,
  onFocus,
  isLargeInput = false,
  onItemSelection = () => {},
  onTypeaheadKeyUp,
  ...props
}) => {
  const { isMobile } = useContext(BootstrapDataContext)

  const [isDisabled, setIsDisabled] = useState(true)
  useEffect(() => {
    setIsDisabled(false)
  }, [])

  return (
    <TypeAheadContainer
      {...props}
      id={id || name}
      dropdownItems={options}
      placeholder={placeholder}
      label={label}
      initialValue={value}
      allowEmptySelection={allowEmptySelection}
      onTypeAheadChange={onChange}
      onItemSelection={item => {
        // hacks because the typeahead API currently
        // doesn't use standard form element props
        setValue(name, valueToString(item))
        setValue(name + 'Object', item)
        onItemSelection({ ...item, selectedPosition: options.indexOf(item) })
      }}
      isUsingErrorMessage={!!error}
      errorMessage={error}
      isUsingPopOver
      selectOnClick
      render={({
        rootProps,
        inputProps,
        errorProps,
        dropdownProps,
        closeDropdown
      }) => (
        <Box {...rootProps}>
          <PopoverContainer
            open={dropdownProps.showDropdown}
            onDismiss={pipe(closeDropdown, disableShadowState)}
            popoverButtonProps={popoverButtonProps}
            onOpen={scrollToTop}
            onClose={restoreScrollPosition}
          >
            <Box
              role='combobox'
              aria-expanded={dropdownProps.showDropdown ? 'true' : 'false'}
              aria-owns='typeahead-list'
              aria-haspopup='listbox'
            >

              <TypeAheadInput
                disabled={isDisabled}
                py={2}
                {...inputProps}
                autoHide={label === placeholder}
                icon={icon}
                id={id || name}
                name={name}
                onFocus={(e) => {
                  if (onFocus) {
                    onFocus(e)
                  }
                }}
                isLargeInput={isLargeInput}
                onTypeaheadKeyUp={onTypeaheadKeyUp}
              />
              {errorProps.errorMessage &&
                errorProps.isUsingErrorMessage && (
                <Tooltip {...errorProps}>{errorProps.errorMessage}</Tooltip>
              )}
              {/* For accessibility reasons for desktop, the results are inside the container with role='combobox' */}
              {!isMobile && <Dropdown {...dropdownProps} minWidth='464px' />}
            </Box>
            {isMobile && <Dropdown {...dropdownProps} />}
          </PopoverContainer>
        </Box>
      )}
    />
  )
}

TypeAhead.propTypes = {
  id: PropTypes.string,
  icon: PropTypes.node,
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  valueToString: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onItemSelection: PropTypes.func,
  options: PropTypes.array,
  error: PropTypes.string,
  popoverButtonProps: PropTypes.object,
  allowEmptySelection: PropTypes.bool,
  disableShadowState: PropTypes.func,
  isLargeInput: PropTypes.bool,
  onTypeaheadKeyUp: PropTypes.func
}

TypeAhead.defaultProps = {
  valueToString: value => value.itemName || '',
  allowEmptySelection: true
}

export default TypeAhead
